/**
 * CommunityPostForm - Forms for posting to Community & Local Life categories
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Users, GraduationCap, Search, HeartHandshake, Gift, 
  Megaphone, Car, HelpingHand, ArrowLeft, ArrowRight, 
  Loader2, CheckCircle, AlertTriangle, Phone, Mail,
  MapPin, Calendar, Clock, DollarSign, Image as ImageIcon
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = {
  'ride-sharing': {
    name: 'Ride Sharing',
    icon: Car,
    color: 'from-green-500 to-emerald-600',
    endpoint: '/api/community/posts/ride-share',
    fields: ['ride_type', 'from_location', 'to_location', 'departure_date', 'departure_time', 'seats_available', 'price_per_seat', 'vehicle_type', 'ac_available']
  },
  'classes-workshops': {
    name: 'Classes & Workshops',
    icon: GraduationCap,
    color: 'from-purple-500 to-pink-600',
    endpoint: '/api/community/posts/class-workshop',
    fields: ['class_type', 'class_format', 'instructor_name', 'duration', 'schedule', 'max_students', 'price_per_session', 'skill_level', 'materials_provided']
  },
  'lost-found': {
    name: 'Lost & Found',
    icon: Search,
    color: 'from-amber-500 to-orange-600',
    endpoint: '/api/community/posts/lost-found',
    fields: ['post_type', 'item_category', 'date_lost_found', 'exact_location', 'identifying_features', 'reward_offered']
  },
  'volunteers': {
    name: 'Volunteers',
    icon: HeartHandshake,
    color: 'from-rose-500 to-red-600',
    endpoint: '/api/community/posts/volunteer',
    fields: ['volunteer_type', 'volunteer_category', 'organization_name', 'commitment_type', 'hours_per_week', 'skills_required', 'training_provided', 'transport_provided']
  },
  'community-help': {
    name: 'Community Help',
    icon: HelpingHand,
    color: 'from-violet-500 to-purple-600',
    endpoint: '/api/community/posts/community-help',
    fields: ['help_type', 'help_category', 'urgency', 'date_needed', 'payment_offered', 'skills_needed', 'helpers_needed']
  },
  'local-groups-clubs': {
    name: 'Local Groups & Clubs',
    icon: Users,
    color: 'from-blue-500 to-indigo-600',
    endpoint: '/api/community/posts/generic',
    fields: ['group_type', 'meeting_frequency', 'meeting_location', 'membership_fee', 'accepts_new_members']
  },
  'charity-donations': {
    name: 'Charity & Donations',
    icon: Gift,
    color: 'from-emerald-500 to-teal-600',
    endpoint: '/api/community/posts/generic',
    fields: ['donation_type', 'donation_category', 'items_list', 'pickup_available', 'deadline']
  },
  'notices-announcements': {
    name: 'Notices & Announcements',
    icon: Megaphone,
    color: 'from-cyan-500 to-blue-600',
    endpoint: '/api/community/posts/generic',
    fields: ['notice_type', 'priority', 'effective_from', 'effective_until', 'affected_areas']
  }
};

// Category selector component
const CategorySelector = ({ selected, onSelect }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {Object.entries(CATEGORIES).map(([slug, config]) => {
      const Icon = config.icon;
      return (
        <Card 
          key={slug}
          className={`cursor-pointer transition-all hover:shadow-lg ${
            selected === slug ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
          }`}
          onClick={() => onSelect(slug)}
          data-testid={`category-${slug}`}
        >
          <CardContent className="p-4 text-center">
            <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-r ${config.color} flex items-center justify-center mb-2`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <p className="font-medium text-sm">{config.name}</p>
          </CardContent>
        </Card>
      );
    })}
  </div>
);

// Ride Sharing Form
const RideShareForm = ({ formData, setFormData, errors }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Type *</Label>
        <Select value={formData.ride_type || ''} onValueChange={(v) => setFormData({...formData, ride_type: v})}>
          <SelectTrigger data-testid="ride-type-select">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="offering">Offering a Ride</SelectItem>
            <SelectItem value="seeking">Looking for a Ride</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Vehicle Type</Label>
        <Input 
          placeholder="e.g., Toyota Axio" 
          value={formData.vehicle_type || ''} 
          onChange={(e) => setFormData({...formData, vehicle_type: e.target.value})}
        />
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>From *</Label>
        <Input 
          placeholder="Starting location" 
          value={formData.from_location || ''} 
          onChange={(e) => setFormData({...formData, from_location: e.target.value})}
          data-testid="from-location"
        />
      </div>
      <div>
        <Label>To *</Label>
        <Input 
          placeholder="Destination" 
          value={formData.to_location || ''} 
          onChange={(e) => setFormData({...formData, to_location: e.target.value})}
          data-testid="to-location"
        />
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Date *</Label>
        <Input 
          type="date" 
          value={formData.departure_date || ''} 
          onChange={(e) => setFormData({...formData, departure_date: e.target.value})}
        />
      </div>
      <div>
        <Label>Time *</Label>
        <Input 
          type="time" 
          value={formData.departure_time || ''} 
          onChange={(e) => setFormData({...formData, departure_time: e.target.value})}
        />
      </div>
      <div>
        <Label>Seats *</Label>
        <Input 
          type="number" 
          min="1" 
          max="6" 
          value={formData.seats_available || 1} 
          onChange={(e) => setFormData({...formData, seats_available: parseInt(e.target.value)})}
        />
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Price per Seat</Label>
        <Input 
          type="number" 
          min="0" 
          placeholder="0 for free" 
          value={formData.price_per_seat || ''} 
          onChange={(e) => setFormData({...formData, price_per_seat: parseFloat(e.target.value) || 0})}
        />
      </div>
      <div className="flex items-end gap-4">
        <div className="flex items-center gap-2">
          <Checkbox 
            id="ac" 
            checked={formData.ac_available !== false}
            onCheckedChange={(c) => setFormData({...formData, ac_available: c})}
          />
          <Label htmlFor="ac">AC Available</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="luggage" 
            checked={formData.luggage_space !== false}
            onCheckedChange={(c) => setFormData({...formData, luggage_space: c})}
          />
          <Label htmlFor="luggage">Luggage Space</Label>
        </div>
      </div>
    </div>
  </div>
);

// Class/Workshop Form
const ClassWorkshopForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Class Type *</Label>
        <Select value={formData.class_type || ''} onValueChange={(v) => setFormData({...formData, class_type: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="language">Language</SelectItem>
            <SelectItem value="music">Music</SelectItem>
            <SelectItem value="art">Art</SelectItem>
            <SelectItem value="cooking">Cooking</SelectItem>
            <SelectItem value="technology">Technology</SelectItem>
            <SelectItem value="fitness">Fitness</SelectItem>
            <SelectItem value="crafts">Crafts</SelectItem>
            <SelectItem value="academic">Academic</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Format *</Label>
        <Select value={formData.class_format || 'in_person'} onValueChange={(v) => setFormData({...formData, class_format: v})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="in_person">In Person</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="hybrid">Hybrid</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    <div>
      <Label>Instructor Name *</Label>
      <Input 
        placeholder="Your name or instructor's name" 
        value={formData.instructor_name || ''} 
        onChange={(e) => setFormData({...formData, instructor_name: e.target.value})}
      />
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Duration *</Label>
        <Input 
          placeholder="e.g., 8 weeks, 2 hours" 
          value={formData.duration || ''} 
          onChange={(e) => setFormData({...formData, duration: e.target.value})}
        />
      </div>
      <div>
        <Label>Schedule *</Label>
        <Input 
          placeholder="e.g., Saturdays 3PM-5PM" 
          value={formData.schedule || ''} 
          onChange={(e) => setFormData({...formData, schedule: e.target.value})}
        />
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Max Students</Label>
        <Input 
          type="number" 
          min="1" 
          value={formData.max_students || ''} 
          onChange={(e) => setFormData({...formData, max_students: parseInt(e.target.value) || null})}
        />
      </div>
      <div>
        <Label>Price per Session *</Label>
        <Input 
          type="number" 
          min="0" 
          value={formData.price_per_session || ''} 
          onChange={(e) => setFormData({...formData, price_per_session: parseFloat(e.target.value) || 0})}
        />
      </div>
      <div>
        <Label>Skill Level</Label>
        <Select value={formData.skill_level || 'beginner'} onValueChange={(v) => setFormData({...formData, skill_level: v})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
            <SelectItem value="all">All Levels</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    <div className="flex items-center gap-2">
      <Checkbox 
        id="materials" 
        checked={formData.materials_provided === true}
        onCheckedChange={(c) => setFormData({...formData, materials_provided: c})}
      />
      <Label htmlFor="materials">Materials/Equipment Provided</Label>
    </div>
  </div>
);

// Lost & Found Form
const LostFoundForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Type *</Label>
        <Select value={formData.post_type || ''} onValueChange={(v) => setFormData({...formData, post_type: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Lost or Found?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lost">Lost Item</SelectItem>
            <SelectItem value="found">Found Item</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Category *</Label>
        <Select value={formData.item_category || ''} onValueChange={(v) => setFormData({...formData, item_category: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Item type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pet">Pet</SelectItem>
            <SelectItem value="electronics">Electronics</SelectItem>
            <SelectItem value="documents">Documents/ID</SelectItem>
            <SelectItem value="jewelry">Jewelry</SelectItem>
            <SelectItem value="keys">Keys</SelectItem>
            <SelectItem value="wallet">Wallet/Purse</SelectItem>
            <SelectItem value="clothing">Clothing</SelectItem>
            <SelectItem value="bag">Bag/Luggage</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Date Lost/Found *</Label>
        <Input 
          type="date" 
          value={formData.date_lost_found || ''} 
          onChange={(e) => setFormData({...formData, date_lost_found: e.target.value})}
        />
      </div>
      <div>
        <Label>Reward Offered</Label>
        <Input 
          type="number" 
          min="0" 
          placeholder="0 if no reward" 
          value={formData.reward_offered || ''} 
          onChange={(e) => setFormData({...formData, reward_offered: parseFloat(e.target.value) || 0})}
        />
      </div>
    </div>
    
    <div>
      <Label>Exact Location *</Label>
      <Input 
        placeholder="Where was it lost/found?" 
        value={formData.exact_location || ''} 
        onChange={(e) => setFormData({...formData, exact_location: e.target.value})}
      />
    </div>
    
    <div>
      <Label>Identifying Features *</Label>
      <Textarea 
        placeholder="Describe unique features to help identify the item (color, marks, brand, etc.)" 
        value={formData.identifying_features || ''} 
        onChange={(e) => setFormData({...formData, identifying_features: e.target.value})}
        rows={3}
      />
    </div>
  </div>
);

// Volunteer Form
const VolunteerForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Type *</Label>
        <Select value={formData.volunteer_type || ''} onValueChange={(v) => setFormData({...formData, volunteer_type: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="seeking">Seeking Volunteers</SelectItem>
            <SelectItem value="offering">Offering to Volunteer</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Category *</Label>
        <Select value={formData.volunteer_category || ''} onValueChange={(v) => setFormData({...formData, volunteer_category: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="education">Education</SelectItem>
            <SelectItem value="healthcare">Healthcare</SelectItem>
            <SelectItem value="environment">Environment</SelectItem>
            <SelectItem value="animals">Animals</SelectItem>
            <SelectItem value="elderly">Elderly Care</SelectItem>
            <SelectItem value="children">Children</SelectItem>
            <SelectItem value="disaster_relief">Disaster Relief</SelectItem>
            <SelectItem value="community">Community</SelectItem>
            <SelectItem value="events">Events</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    <div>
      <Label>Organization Name</Label>
      <Input 
        placeholder="If applicable" 
        value={formData.organization_name || ''} 
        onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
      />
    </div>
    
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Commitment *</Label>
        <Select value={formData.commitment_type || ''} onValueChange={(v) => setFormData({...formData, commitment_type: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="one-time">One-time</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="ongoing">Ongoing</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Hours per Week</Label>
        <Input 
          type="number" 
          min="1" 
          value={formData.hours_per_week || ''} 
          onChange={(e) => setFormData({...formData, hours_per_week: parseInt(e.target.value) || null})}
        />
      </div>
    </div>
    
    <div className="flex gap-4">
      <div className="flex items-center gap-2">
        <Checkbox 
          id="training" 
          checked={formData.training_provided === true}
          onCheckedChange={(c) => setFormData({...formData, training_provided: c})}
        />
        <Label htmlFor="training">Training Provided</Label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox 
          id="transport" 
          checked={formData.transport_provided === true}
          onCheckedChange={(c) => setFormData({...formData, transport_provided: c})}
        />
        <Label htmlFor="transport">Transport Provided</Label>
      </div>
    </div>
  </div>
);

// Community Help Form
const CommunityHelpForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Type *</Label>
        <Select value={formData.help_type || ''} onValueChange={(v) => setFormData({...formData, help_type: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="seeking">Need Help</SelectItem>
            <SelectItem value="offering">Offering Help</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Category *</Label>
        <Select value={formData.help_category || ''} onValueChange={(v) => setFormData({...formData, help_category: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="moving">Moving/Lifting</SelectItem>
            <SelectItem value="repairs">Home Repairs</SelectItem>
            <SelectItem value="gardening">Gardening</SelectItem>
            <SelectItem value="errands">Errands</SelectItem>
            <SelectItem value="elderly_care">Elderly Care</SelectItem>
            <SelectItem value="child_care">Child Care</SelectItem>
            <SelectItem value="pet_care">Pet Care</SelectItem>
            <SelectItem value="tutoring">Tutoring</SelectItem>
            <SelectItem value="tech_help">Tech Help</SelectItem>
            <SelectItem value="translation">Translation</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-4">
      <div>
        <Label>Urgency</Label>
        <Select value={formData.urgency || 'normal'} onValueChange={(v) => setFormData({...formData, urgency: v})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="flexible">Flexible</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Date Needed</Label>
        <Input 
          type="date" 
          value={formData.date_needed || ''} 
          onChange={(e) => setFormData({...formData, date_needed: e.target.value})}
        />
      </div>
      <div>
        <Label>Helpers Needed</Label>
        <Input 
          type="number" 
          min="1" 
          value={formData.helpers_needed || 1} 
          onChange={(e) => setFormData({...formData, helpers_needed: parseInt(e.target.value) || 1})}
        />
      </div>
    </div>
    
    <div>
      <Label>Payment Offered</Label>
      <Input 
        type="number" 
        min="0" 
        placeholder="0 for volunteer help" 
        value={formData.payment_offered || ''} 
        onChange={(e) => setFormData({...formData, payment_offered: parseFloat(e.target.value) || 0})}
      />
    </div>
  </div>
);

// Generic Form (for local groups, charity, notices)
const GenericForm = ({ formData, setFormData, category }) => (
  <div className="space-y-4">
    <Alert>
      <AlertDescription>
        Fill in the basic information below. Category-specific fields will be added soon.
      </AlertDescription>
    </Alert>
  </div>
);

// Main Component
export default function CommunityPostForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country, countryConfig } = useCountry();
  
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    contact_phone: '',
    contact_email: '',
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdPost, setCreatedPost] = useState(null);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Get category config
  const categoryConfig = selectedCategory ? CATEGORIES[selectedCategory] : null;
  
  // Form validation
  const validateForm = () => {
    if (!formData.title || formData.title.length < 5) {
      setError('Title must be at least 5 characters');
      return false;
    }
    if (!formData.description || formData.description.length < 20) {
      setError('Description must be at least 20 characters');
      return false;
    }
    if (!formData.location) {
      setError('Location is required');
      return false;
    }
    if (!formData.contact_phone) {
      setError('Phone number is required');
      return false;
    }
    return true;
  };
  
  // Submit form
  const handleSubmit = async () => {
    setError('');
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const endpoint = categoryConfig?.endpoint || '/api/community/posts/generic';
      
      // Prepare payload based on category
      const payload = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        country: country,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email || undefined,
        images: formData.images,
        ...formData  // Include all category-specific fields
      };
      
      // For generic endpoint, add category
      if (endpoint === '/api/community/posts/generic') {
        payload.category = selectedCategory;
        payload.extra_fields = {};
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create post');
      }
      
      setCreatedPost(data);
      setSuccess(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Render category-specific form
  const renderCategoryForm = () => {
    switch (selectedCategory) {
      case 'ride-sharing':
        return <RideShareForm formData={formData} setFormData={setFormData} />;
      case 'classes-workshops':
        return <ClassWorkshopForm formData={formData} setFormData={setFormData} />;
      case 'lost-found':
        return <LostFoundForm formData={formData} setFormData={setFormData} />;
      case 'volunteers':
        return <VolunteerForm formData={formData} setFormData={setFormData} />;
      case 'community-help':
        return <CommunityHelpForm formData={formData} setFormData={setFormData} />;
      default:
        return <GenericForm formData={formData} setFormData={setFormData} category={selectedCategory} />;
    }
  };
  
  // Success screen
  if (success && createdPost) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Post Created Successfully!</h2>
              <p className="text-gray-600 mb-6">
                {createdPost.status === 'active' 
                  ? 'Your post is now live and visible to the community.'
                  : 'Your post is pending review and will be visible once approved.'}
              </p>
              
              <div className="flex justify-center gap-4">
                <Button onClick={() => navigate(`/category/${selectedCategory}`)}>
                  View Category
                </Button>
                <Button variant="outline" onClick={() => {
                  setSuccess(false);
                  setCreatedPost(null);
                  setFormData({
                    title: '',
                    description: '',
                    location: '',
                    contact_phone: '',
                    contact_email: '',
                    images: []
                  });
                  setStep(1);
                }}>
                  Create Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold">Post to Community</h1>
          <p className="text-gray-600">Share with your local community</p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-20 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        
        {/* Step 1: Category Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Category</CardTitle>
              <CardDescription>Choose the type of post you want to create</CardDescription>
            </CardHeader>
            <CardContent>
              <CategorySelector selected={selectedCategory} onSelect={(cat) => {
                setSelectedCategory(cat);
              }} />
              
              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!selectedCategory}
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: Basic Info + Category Fields */}
        {step === 2 && categoryConfig && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-gradient-to-r ${categoryConfig.color}`}>
                  <categoryConfig.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>{categoryConfig.name}</CardTitle>
                  <CardDescription>Fill in the details for your post</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Fields */}
              <div className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input 
                    placeholder="A clear, descriptive title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    data-testid="post-title"
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea 
                    placeholder="Provide detailed information..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={4}
                    data-testid="post-description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Location *</Label>
                    <Input 
                      placeholder="City or area"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input 
                      placeholder="Contact number"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Email (optional)</Label>
                  <Input 
                    type="email"
                    placeholder="email@example.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  />
                </div>
              </div>
              
              {/* Category-Specific Fields */}
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4">{categoryConfig.name} Details</h3>
                {renderCategoryForm()}
              </div>
              
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setStep(3)}>
                  Preview
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Review & Submit */}
        {step === 3 && categoryConfig && (
          <Card>
            <CardHeader>
              <CardTitle>Review Your Post</CardTitle>
              <CardDescription>Make sure everything looks correct before submitting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Preview */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${categoryConfig.color}`}>
                    <categoryConfig.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <Badge className="mb-2">{categoryConfig.name}</Badge>
                    <h3 className="text-xl font-bold">{formData.title || 'Untitled'}</h3>
                    <p className="text-gray-600 mt-2">{formData.description || 'No description'}</p>
                    
                    <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {formData.location || 'No location'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {formData.contact_phone || 'No phone'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Disclaimer */}
              <Alert>
                <AlertDescription>
                  By posting, you agree to our community guidelines. Posts may be reviewed before appearing publicly.
                </AlertDescription>
              </Alert>
              
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={handleSubmit} disabled={loading} data-testid="submit-post-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Post
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
